import React, { useState, useMemo } from "react";
import Tooltip from "@material-ui/core/Tooltip";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import AddIcon from "@material-ui/icons/Add";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import BubbleChartIcon from "@material-ui/icons/BubbleChart";
import CompareArrowsIcon from "@material-ui/icons/CompareArrows";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { useStyles } from "./styles";


export default function ProjectList({ projects, selectedId, onSelect, onCreateNew, onEdit, onDelete, strings }) {
    const classes = useStyles();
    const tc = strings.textComparisons || {};

    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuProject, setMenuProject] = useState(null);
    const [sortKey, setSortKey] = useState("name");
    const [sortAsc, setSortAsc] = useState(true);

    const handleMenuOpen = (e, proj) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
        setMenuProject(proj);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setMenuProject(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        if (onEdit && menuProject) onEdit(menuProject);
    };

    const handleDelete = () => {
        handleMenuClose();
        if (onDelete && menuProject) onDelete(menuProject);
    };

    const handleSortClick = (key) => {
        if (key === sortKey) {
            setSortAsc(a => !a);
        } else {
            setSortKey(key);
            setSortAsc(key !== "texts");
        }
    };

    const sortIndicator = (key) => (
        <span style={{ opacity: key === sortKey ? 1 : 0, marginRight: 4 }}>
            {sortAsc ? "🔼" : "🔽"}
        </span>
    );

    const sorted = useMemo(() => {
        return [...projects].sort((a, b) => {
            let cmp;
            if (sortKey === "name") cmp = (a.Name || "").localeCompare(b.Name || "");
            else if (sortKey === "type") cmp = (!!a.ProjectId ? 1 : 0) - (!!b.ProjectId ? 1 : 0);
            else {
                const aT = a.LibraryTexts ?? 0;
                const bT = b.LibraryTexts ?? 0;
                cmp = aT - bT;
            }
            return sortAsc ? cmp : -cmp;
        });
    }, [projects, sortKey, sortAsc]);

    return (
        <div className={classes.leftPanel}>
            <div className={classes.newComparisonBar}>
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={onCreateNew} fullWidth>
                    {tc.newComparison || "New Comparison"}
                </Button>
            </div>
            <div className={classes.tableScroll}>
                <table className={classes.table}>
                    <thead>
                        <tr>
                            <th
                                className={classes.thCenter}
                                onClick={() => handleSortClick("type")}
                                style={{ cursor: "pointer", userSelect: "none", width: 1, padding: "7px 4px" }}
                            >
                                <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    {sortIndicator("type")}<span>{tc.type || "Type"}</span>
                                </span>
                            </th>
                            <th
                                className={classes.th}
                                onClick={() => handleSortClick("name")}
                                style={{ cursor: "pointer", userSelect: "none" }}
                            >
                                <span style={{ display: "flex", alignItems: "center" }}>
                                    {sortIndicator("name")}<span>{tc.name || "Name"}</span>
                                </span>
                            </th>
                            <th
                                className={classes.thCenter}
                                onClick={() => handleSortClick("texts")}
                                style={{ cursor: "pointer", userSelect: "none" }}
                            >
                                <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    {sortIndicator("texts")}<span>{tc.texts || "Texts"}</span>
                                </span>
                            </th>
                            <th className={classes.thCenter} style={{ width: 1, padding: 0 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((proj) => {
                            const isActive = proj.Id === selectedId;
                            const isSynced = !!proj.ProjectId;
                            return (
                                <tr
                                    key={proj.Id}
                                    className={isActive ? classes.rowActive : classes.row}
                                    onClick={() => onSelect(proj.Id)}
                                >
                                    <Tooltip title={isSynced ? (tc.syncedWithProject || "Synchronized with project") : (tc.independentComparison || "Independent comparison")} placement="right">
                                        <td style={{ padding: "0 4px 0 8px", textAlign: "center", width: 1, whiteSpace: "nowrap", color: isActive ? "inherit" : "#888" }}>
                                            {isSynced
                                                ? <BubbleChartIcon style={{ fontSize: 22, verticalAlign: "middle" }} />
                                                : <CompareArrowsIcon style={{ fontSize: 22, verticalAlign: "middle" }} />}
                                        </td>
                                    </Tooltip>
                                    <Tooltip title={proj.Name} placement="right">
                                        <td className={classes.td}>{proj.Name}</td>
                                    </Tooltip>
                                    <td className={classes.tdCenter}>
                                        {proj.Status === 'Aktiv'
                                            ? `?/${proj.LibraryTexts ?? 0}`
                                            : `${proj.MatchCount ?? 0}/${proj.LibraryTexts ?? 0}`}
                                    </td>
                                    <td style={{ padding: "0 6px 0 0", width: 1, whiteSpace: "nowrap" }}>
                                        <IconButton
                                            size="small"
                                            style={{ padding: 2 }}
                                            onClick={(e) => handleMenuOpen(e, proj)}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <Typography variant="body2" color="secondary" style={{ padding: "8px 10px", lineHeight: 1.4 }}>
                {tc.projectLevelHint || "New text comparisons can also be created by enabling Similarity Check at project level."}
            </Typography>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MenuItem onClick={handleEdit}>
                    <EditIcon fontSize="small" style={{ marginRight: 8 }} />
                    {tc.menuEdit || "Edit"}
                </MenuItem>
                <MenuItem onClick={handleDelete} style={{ color: "#c62828" }}>
                    <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
                    {tc.menuDelete || "Delete"}
                </MenuItem>
            </Menu>
        </div>
    );
}
